import React, { useEffect, useState } from 'react';
import { useStore } from '@/components/store/Store';
import { BlockComponent } from '@/editor/lib/declarations/EditorComponentTypes';
import Container from '@/components/Container';
import SelectInput from '@/components/forms/SelectInput';
import { Typography, Button } from '@mui/material';
import { DimuOwner } from '@/declarations/models/DimuOwner';
import { Api } from '@/services/Api';
import M24PageFinder from '@/components/Finder/M24PageFinder/M24PageFinder';
import { useController } from 'react-hook-form';
import { BlockSpecificPage } from '@/editor/lib/declarations/BlockSpecificPage';
import { DMSchoolBlockModel } from '@/declarations/models/blocks/DMBlock';
import { useCurrentBlock } from '@/editor/PageEditor/CurrentBlockProvider';
import { useTranslation } from 'react-i18next';
import SwitchInput from '@/components/forms/SwitchInput';
import { DMSchoolBlockListingSelects } from '@/editor/PageEditor/EditorBlock/DMSchoolBlock/DMSchoolBlockListingSelects';
import Styles from '@/assets/js/Styles';
import DMSchoolGradeSubjectSelects from '@/editor/PageEditor/EditorBlock/DMSchoolBlock/DMSchoolGradeSubjectSelects';
import ToggleSortingFilteringSettings from '@/editor/PageEditor/EditorBlock/DMSchoolBlock/ToggleSortingFilteringSettings';
import PageListManualBlockItem from '@/editor/PageEditor/EditorBlock/PageListBlocks/PageListManualBlockItem';
import { noop } from '@/utils/functions';
import TextInput from '@/components/forms/TextInput';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

const autocompleteItemStyle = (selected: boolean) => ({
  backgroundColor: selected ? 'rgba(0,0,0,0.16) !important' : undefined,
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.08) !important',
  },
});

const GRADES = ['kindergarten', 'grade_two', 'grade_four', 'grade_seven', 'grade_ten', 'vg_one', 'vg_two', 'vg_three'];

const SUBJECTS = [
  'Samfunnsfag',
  'Historie',
  'Naturfag',
  'Norsk',
  'Kunst og håndverk',
  'Mat og helse',
  'Kulturarv',
  'KRLE',
  'Kroppsøving',
];

const getOptionLabel = (option: DimuOwner) => `${option.name} (${option.identifier})`;

export const DMSchoolBlock: BlockComponent = () => {
  const { t: tComp } = useTranslation('components');
  const [allDimuOwners, setAllDimuOwners] = useState<Array<DimuOwner>>([]);
  const [selectedOwners, setSelectedOwners] = useState<Array<DimuOwner>>([]);
  const { blockPath } = useCurrentBlock();
  const {
    state: { currentUser },
  } = useStore();
  const isSuperAdmin = currentUser?.user?.is_super_admin;

  const mobileGridOptions = ['auto', 'horizontal'];

  const {
    field: { value: ownerCodes, onChange: setOwnerCodes },
  } = useController<BlockSpecificPage<DMSchoolBlockModel>, `${typeof blockPath}.ownerCodes`>({
    name: `${blockPath}.ownerCodes`,
  });

  const {
    field: { value: grades = [], onChange: setGrades },
  } = useController<BlockSpecificPage<DMSchoolBlockModel>, `${typeof blockPath}.schoolGrades`>({
    name: `${blockPath}.schoolGrades`,
  });

  const {
    field: { value: subjects = [], onChange: setSubjects },
  } = useController<BlockSpecificPage<DMSchoolBlockModel>, `${typeof blockPath}.schoolSubjects`>({
    name: `${blockPath}.schoolSubjects`,
  });

  useEffect(() => {
    const ctx = Api.getDimuOwners();
    ctx
      .fetchDirect(null)
      .then((res) => {
        if (res?.owners) {
          setAllDimuOwners(res.owners);
          if (ownerCodes) {
            const ownerList = ownerCodes.split(',');
            setSelectedOwners(res.owners.filter((o) => ownerList.some((oc) => oc === o.identifier)));
          }
        }
      })
      .finally(ctx.abort);
  }, [ownerCodes]);

  const handleOwnerCodesChanged = (owners: Array<DimuOwner>) => {
    setSelectedOwners(owners);
    setOwnerCodes(owners.map((o) => o.identifier).join(','));
  };

  const handleGradesChanged = (DMSchoolGrades: Array<string>) => {
    setGrades(DMSchoolGrades);
  };

  const handleSubjectsChanged = (DMSchoolGSubjects: Array<string>) => {
    setSubjects(DMSchoolGSubjects);
  };

  const [pageFinderOpen, setPageFinderOpen] = useState(false);
  const {
    field: { value: showAllLinkPage, onChange: onChangeShowAllLinkPage },
  } = useController<BlockSpecificPage<DMSchoolBlockModel>, `${typeof blockPath}.showAllLinkPage`>({
    name: `${blockPath}.showAllLinkPage`,
  });

  return (
    <Container gap={2} column top left fullWidth>
      <OutlinedContainer
        fullWidth
        column
        left
        label={tComp('DMBlock.SearchConfig')}
        sx={{
          backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
          paddingTop: '2em',
        }}>
        <DMSchoolGradeSubjectSelects
          tComp={tComp}
          allDimuOwners={allDimuOwners}
          selectedOwners={selectedOwners}
          allDMSchoolGrades={GRADES}
          selectedDMSchoolGrades={Array.isArray(grades) ? grades : []}
          allDMSchoolSubjects={SUBJECTS}
          selectedDMSchoolSubjects={Array.isArray(subjects) ? subjects : []}
          handleOwnerCodesChanged={handleOwnerCodesChanged}
          handleDMSchoolGradesChanged={handleGradesChanged}
          handleDMSchoolSubjectsChanged={handleSubjectsChanged}
          getOptionLabel={getOptionLabel}
          autocompleteItemStyle={autocompleteItemStyle}
        />
        <Container gap={2} column top left fullWidth>
          <Typography variant='subtitle2'>{tComp('DMBlock.ShowSchoolTypes')}:</Typography>
          <Container>
            <div>
              <SwitchInput
                path={`${blockPath}.includeDigitalResources`}
                label={`${tComp('DMBlock.DigitalResources')}`}
              />
            </div>
            <div>
              <SwitchInput
                path={`${blockPath}.includePhysicalResources`}
                label={`${tComp('DMBlock.PhysicalResources')}`}
              />
            </div>
          </Container>
        </Container>
        <DMSchoolBlockListingSelects blockPath={blockPath} />
      </OutlinedContainer>

      <OutlinedContainer fullWidth column left label={tComp('PageListManualBlock.FilteringOptions')}>
        <ToggleSortingFilteringSettings tComp={tComp} blockPath={blockPath} />
      </OutlinedContainer>
      <OutlinedContainer fullWidth column left label={tComp('KPBlock.DisplayOptionsModuleLabel')}>
        <Container gap={2} left fullWidth>
          <div style={{ width: '250px' }}>
            <SelectInput
              hideNoSelectionOption
              defaultValue='auto'
              options={mobileGridOptions}
              getOptionKey={(o) => `mobileGrid-${o}`}
              getOptionLabel={(o) => tComp(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
              path={`${blockPath}.gridMobileBehaviour`}
              label={tComp('CardModuleBlock.GridMobileBehaviourLabel')}
            />
          </div>
          <SwitchInput
            fullWidth={false}
            path={`${blockPath}.showInSameTab`}
            label={tComp('DMFilterSearch.ShowInSameTab')}
          />

          <Container column left sx={{ width: '350px' }}>
            <SwitchInput path={`${blockPath}.integratedView`} label={tComp('KPBlock.IntegratedView')} />
          </Container>
        </Container>
      </OutlinedContainer>
      {/*
      <OutlinedContainer fullWidth column left label={tComp('DMBlock.DmLinkLabel')}>
        <Container fullWidth left pt={1} gap={2}>
          <TextInput path={`${blockPath}.dmLink`} defaultValue='' label={tComp('DMBlock.DmLink')} size='small' />
          <TextInput path={`${blockPath}.dmLinkText`} defaultValue='' label={tComp('DMBlock.DmLinkText')} size='small' />
        </Container>
      </OutlinedContainer>
      */}
      <M24PageFinder
        hideSiteSelector
        open={pageFinderOpen}
        onClose={() => setPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeShowAllLinkPage(pages[0].source);
          }
        }}
      />
      <OutlinedContainer fullWidth column left label={tComp('EventsBlock.ShowAllLinkPageTitle')}>
        {showAllLinkPage && (
          <PageListManualBlockItem
            index={0}
            onReorder={noop}
            onDelete={() => onChangeShowAllLinkPage(null)}
            overrideItemPath={`${blockPath}.showAllLinkPage`}
            color={Styles.Colors.LIGHT_GREY}
            hideHighlightCheckbox
            hideSkinSelector
            disableReordering
          />
        )}
        <Button onClick={() => setPageFinderOpen(true)} variant='outlined' color='secondary'>
          {tComp('EventsBlock.ShowAllLinkPageButtonLabel')}
        </Button>
      </OutlinedContainer>
    </Container>
  );
};
